import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  addSectionQuestion,
  importSectionQuestions,
} from "../../services/sectionQuestionsService";

import { FaPlusCircle, FaFileCsv, FaDownload } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { FiUpload } from "react-icons/fi";

export const AddAllSectionLayer = () => {
  const [section, setSection] = useState("A");
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Configuration for 1-second toasts
  const toastConfig = { autoClose: 1000 };

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const addOptionField = () => {
    setOptions([...options, ""]);
  };

  const removeOptionField = (index) => {
    if (options.length <= 2) {
      toast.error("Minimum 2 options required", toastConfig);
      return;
    }
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSingleAdd = async () => {
    if (!question.trim()) {
      toast.error("Enter question first", toastConfig);
      return;
    }

    if (section === "C" && options.some((o) => !o.trim())) {
      toast.error("All options must be filled", toastConfig);
      return;
    }

    setLoading(true);
    try {
      const payload =
        section === "C" ? { question, options } : { question };

      const res = await addSectionQuestion(section, payload);

      if (res?.status) {
        toast.success("Question Added Successfully ✅", toastConfig);
        setQuestion("");
        setOptions(["", ""]);
      } else {
        toast.error(res?.msg || "Failed to add", toastConfig);
      }
    } catch {
      toast.error("Server error", toastConfig);
    }
    setLoading(false);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Select CSV file first", toastConfig);
      return;
    }

    setLoading(true);
    try {
      const res = await importSectionQuestions(section, file);

      if (res?.status) {
        toast.success("Questions Imported Successfully ✅", toastConfig);
        setFile(null);
      } else {
        toast.error(res?.msg || "Import failed", toastConfig);
      }
    } catch {
      toast.error("Server error", toastConfig);
    }
    setLoading(false);
  };

  /* ================= DOWNLOAD SAMPLE ================= */
  const handleDownloadSample = () => {
    let content = "";

    if (section === "A" || section === "B") {
      content = "question\nWho will win today?";
    }

    if (section === "C") {
      content =
        "question,option1,option2,option3,option4\nWho will win today?,Team A,Team B,Draw,Other";
    }

    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${section}_section_sample.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.info("Sample Downloaded", toastConfig);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <h5 className="fw-bold mb-0">
            <FaFileCsv className="me-2" />
            Manage Section Questions
          </h5>
        </div>

        <div className="card-body">
          <div className="row g-3">
            {/* SECTION */}
            <div className="col-12 col-md-4">
              <label className="form-label">Select Section</label>
              <select
                className="form-select"
                value={section}
                onChange={(e) => setSection(e.target.value)}
              >
                <option value="A">Section A</option>
                <option value="B">Section B</option>
                <option value="C">Section C (With Options)</option>
              </select>
            </div>

            {/* QUESTION */}
            <div className="col-12 col-md-8">
              <label className="form-label">Question</label>
              <input
                type="text"
                className="form-control"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
            </div>

            {/* OPTIONS */}
            {section === "C" && (
              <div className="col-12">
                <label className="form-label">Options</label>

                {options.map((opt, index) => (
                  <div key={index} className="row g-2 mb-2">
                    <div className="col-9 col-md-10">
                      <input
                        type="text"
                        className="form-control"
                        value={opt}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                      />
                    </div>
                    <div className="col-3 col-md-2">
                      <button
                        type="button"
                        className="btn btn-danger w-100"
                        onClick={() => removeOptionField(index)}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-outline-primary mt-2"
                  onClick={addOptionField}
                >
                  <FaPlusCircle className="me-2" />
                  Add Option
                </button>
              </div>
            )}

            {/* ADD BUTTON */}
            <div className="col-12 col-md-4">
              <button
                className="btn btn-success w-100"
                onClick={handleSingleAdd}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Question"}
              </button>
            </div>

            <div className="col-12 mt-4 border-top pt-4">
              <label className="form-label fw-bold">Import CSV</label>
            </div>

            <div className="col-12 col-md-6">
              <input
                type="file"
                className="form-control"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>

            <div className="col-12 col-md-3">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={handleDownloadSample}
              >
                <FaDownload className="me-2" />
                Sample CSV
              </button>
            </div>

            {file && (
              <>
                <div className="col-12 col-md-3">
                  <button
                    className="btn btn-danger w-100"
                    onClick={() => setFile(null)}
                  >
                    Remove File
                  </button>
                </div>

                <div className="col-12 mt-2">
                  <div className="form-control bg-light">
                    Selected File: <strong>{file.name}</strong>
                  </div>
                </div>

                <div className="col-12 col-md-4 mt-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleImport}
                    disabled={loading}
                  >
                    <FiUpload className="me-2" />
                    {loading ? "Importing..." : "Start Import"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};